/**
 * 📖 그림책을 **우리 반에 올린다**(선생님 화면·전시장이 볼 수 있게).
 *
 * 🔴 반 코드가 없으면 아무것도 보내지 않는다. 집에서 혼자 쓰는 아이의 이야기는
 * 이 기기 밖으로 나가지 않는다 — 올리는 것은 「수업 중」에만 일어나는 일이다.
 *
 * 🔴 `EDGE_BASE` 를 반드시 앞에 붙인다. dev 에서 웹(5173)과 엣지(8787)가 다른 출처라
 * 상대경로로 부르면 **조용히 실패한다**(랠리에서 한 번 겪은 버그).
 */
import { sanitizeStory, type SavedStory } from "@greg-chick/shared";
import { EDGE_BASE } from "./apiBase";

export interface ClassStoryRow {
  heroCode: string;
  story: SavedStory;
}

/**
 * 보낸 결과 — **성패가 아니라 「다시 시도할 값어치가 있나」**다.
 *
 * 🔴 예전엔 `true/false` 만 돌려줬다. 그래서 📮 우체통이 **서버가 「안 된다」고 해도
 * 2분마다 영영 두드렸다** — 반이 지워졌거나 코드가 깨진 글은 절대 안 올라가는데,
 * 아이 화면은 「아직 못 보냈어요」로 영영 남고 학교 공유기만 두들긴다.
 */
export type PushResult = "ok" | "retry" | "gone";

/** 우리 반에 올린다. 실패해도 이 기기의 글은 그대로다. */
export async function pushStory(
  classCode: string,
  heroCode: string,
  story: SavedStory,
): Promise<PushResult> {
  try {
    const res = await fetch(`${EDGE_BASE}/api/story`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ classCode, heroCode, story }),
    });
    return pushOutcome(res.status);
  } catch {
    // 학교 와이파이가 끊긴 것 — 인터넷이 오면 다시 보낸다(경계 폴백, 불변식 3)
    return "retry";
  }
}

/**
 * 🔴 **어떤 답에 다시 시도할까.**
 *
 * · 429(너무 자주)·5xx(서버 사정) → 기다렸다 다시. 시간이 해결한다
 * · 그 밖의 4xx → **다시 해도 소용없다.** 반이 지워졌거나(410) 코드가 깨졌다(400)
 * · 나머지 → 다시(모르면 지키는 쪽으로 — 아이 글을 함부로 버리지 않는다)
 */
export function pushOutcome(status: number): PushResult {
  if (status >= 200 && status < 300) return "ok";
  if (status === 429 || status >= 500) return "retry";
  if (status >= 400) return "gone";
  return "retry";
}

/**
 * 내가 반에 올려 둔 것 — **왜 없는지까지** 돌려준다.
 *
 * 🔴 「아직 안 썼다」와 「반이 정리됐다」는 다르다. 부모 화면이 이걸 못 가르면
 * 정리된 반에 대고 **「아직 쓴 쪽이 없어요」**라고 말한다 — 아이가 한 학기 만든 것을
 * 없던 일로 만드는 말이다.
 */
export async function pullStoryEx(
  classCode: string,
  heroCode: string,
): Promise<{ story: SavedStory | null; gone: boolean; unknown: boolean; offline: boolean }> {
  const empty = { story: null, gone: false, unknown: false, offline: false };
  try {
    const res = await fetch(
      `${EDGE_BASE}/api/story/${encodeURIComponent(classCode)}/${encodeURIComponent(heroCode)}`,
      { cache: "no-store" },
    );
    if (res.status === 410) return { ...empty, gone: true };
    if (res.status === 404) {
      /**
       * 🔴 **404 도 두 가지다.** 「이 반에 이 아이 글이 아직 없다」와 「그런 반이 아예 없다」.
       * 뭉뚱그리면 부모가 잘못 찍은 QR 을 보고 **아이가 아무것도 안 한 줄 안다.**
       */
      const why = (await res.json().catch(() => null)) as { unknown?: unknown } | null;
      return { ...empty, unknown: why?.unknown === true };
    }
    /**
     * 🔴 **「없다」와 「지금 못 물어봤다」는 다르다.**
     *
     * 예전엔 404 가 아닌 실패(500·503·429)도 「아직 안 썼다」로 뭉뚱그렸다. 그러면 서버가
     * 잠깐 아픈 동안 종이 QR 을 찍은 부모가 **아이가 아무것도 안 한 줄 안다** —
     * 사실이 아니고, 아이가 한 학기 만든 것을 없던 일로 만드는 말이다.
     * (히어로 복원은 이미 이렇게 가르고 있었다 — `getHeroEx` 의 `not_found`/`offline`.)
     */
    if (!res.ok) return { ...empty, offline: true };
    const body = (await res.json()) as { story?: unknown };
    return { ...empty, story: sanitizeStory(body.story) };
  } catch {
    return { ...empty, offline: true }; // 끊겼다 — 없다고 단정하지 않는다
  }
}

/** 내가 반에 올려 둔 것을 도로 가져온다(다른 기기·다음 주 수업). 없으면 null. */
export async function pullStory(classCode: string, heroCode: string): Promise<SavedStory | null> {
  try {
    const res = await fetch(
      `${EDGE_BASE}/api/story/${encodeURIComponent(classCode)}/${encodeURIComponent(heroCode)}`,
      { cache: "no-store" },
    );
    if (!res.ok) return null;
    const body = (await res.json()) as { story?: unknown };
    return sanitizeStory(body.story);
  } catch {
    return null;
  }
}

/**
 * 우리 반 전체 — **왜 비었는지까지**.
 *
 * 🔴 「아직 아무도 안 썼다」와 「반이 정리됐다」는 다르다. 뭉뚱그리면 전시장이
 * 정리된 반에 대고 「아직 쓴 친구가 없어요」라고 말한다.
 */
export async function pullClassStoriesEx(
  classCode: string,
): Promise<{
  rows: ClassStoryRow[];
  gone: boolean;
  cut: boolean;
  unknown: boolean;
  /**
   * 🔴 **지금 못 물어봤다**(500·503·429·끊김). 한 아이 것을 볼 때는 이미 가르고 있었는데
   * (`pullStoryEx` 의 `offline`) **반 전체를 볼 때는 안 가르고 있었다** — 그래서 서버가
   * 아픈 동안 전시장이 「아직 걸린 그림책이 없어요」라고 말했다. 아이에게는 「친구들이
   * 아무것도 안 썼다」로 들린다.
   */
  offline: boolean;
}> {
  const empty = {
    rows: [] as ClassStoryRow[],
    gone: false,
    cut: false,
    unknown: false,
    offline: false,
  };
  try {
    const res = await fetch(`${EDGE_BASE}/api/story/${encodeURIComponent(classCode)}`, {
      cache: "no-store",
    });
    if (res.status === 410) return { ...empty, gone: true };
    if (!res.ok) return { ...empty, offline: true };
    const body = (await res.json()) as {
      stories?: { heroCode?: unknown; story?: unknown }[];
      cut?: unknown;
      unknown?: unknown;
    };
    return {
      // 🔴 서버가 잘랐으면 그대로 전한다 — 전시장에서 「내 그림책이 없어요」를 겪게 두지 않는다
      cut: body.cut === true,
      // 🔴 **아무 흔적도 없는 코드**(= 잘못 친 코드). 「아직 아무도 안 썼어요」와 다르다
      unknown: body.unknown === true,
      rows: (body.stories ?? [])
        .filter((r): r is { heroCode: string; story: unknown } => typeof r.heroCode === "string")
        .map((r) => ({ heroCode: r.heroCode, story: sanitizeStory(r.story) })),
      gone: false,
      offline: false,
    };
  } catch {
    // 🔴 끊김도 「없다」가 아니다 — 화면이 그렇게 말하게 표시를 달아 보낸다
    return { ...empty, offline: true };
  }
}
